import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  AlertTriangle, Shield, MapPin, Clock, User, Camera, Plus,
  CheckCircle, XCircle, Loader2, AlertCircle, Siren, Upload, Image as ImageIcon,
  Search, MessageSquare, Send, Eye, EyeOff, Mail, Phone, Smartphone
} from 'lucide-react';
import api from '@/lib/api';
import LocalCameraCapture from './LocalCameraCapture';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const SafetyCenter = ({ eventId, isAdmin = false }) => {
  const queryClient = useQueryClient();
  const [showReportIncident, setShowReportIncident] = useState(false);
  const [showTrackIncident, setShowTrackIncident] = useState(false);
  const [showRespondDialog, setShowRespondDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [filter, setFilter] = useState('open');
  const [trackingCode, setTrackingCode] = useState('');
  const [trackedIncident, setTrackedIncident] = useState(null);
  const [trackingError, setTrackingError] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [responseStatus, setResponseStatus] = useState('');
  const [notifyReporter, setNotifyReporter] = useState(true);
  
  const [newIncident, setNewIncident] = useState({
    title: '',
    description: '',
    location: '',
    image_url: '',
    is_anonymous: true,
    reporter_name: '',
    reporter_email: '',
    reporter_phone: '',
    severity: 'medium'
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const photoInputRef = useRef(null);

  const { data: incidents, isLoading } = useQuery({
    queryKey: ['incidents', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/incidents`).then(r => r.data),
    enabled: isAdmin,
    refetchInterval: 10000,
  });

  // Public incident submission (no auth required)
  const reportMutation = useMutation({
    mutationFn: (data) => api.post(`/events/${eventId}/incidents/public`, data),
    onSuccess: (response) => {
      toast.success(
        <div>
          <p className="font-semibold">Incident reported successfully!</p>
          <p className="text-sm mt-1">Tracking Code: <span className="font-mono font-bold">{response.data.tracking_code}</span></p>
        </div>,
        { duration: 10000 }
      );
      setShowReportIncident(false);
      setNewIncident({
        title: '',
        description: '',
        location: '',
        image_url: '',
        is_anonymous: true,
        reporter_name: '',
        reporter_email: '',
        reporter_phone: '',
        severity: 'medium'
      });
      setPhotoPreview(null);
      queryClient.invalidateQueries(['incidents', eventId]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to submit incident report');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ incidentId, updates }) => api.put(`/admin/events/${eventId}/incidents/${incidentId}`, updates),
    onSuccess: () => {
      toast.success('Incident updated');
      queryClient.invalidateQueries(['incidents', eventId]);
    },
  });

  const respondMutation = useMutation({
    mutationFn: ({ incidentId, data }) => api.post(`/admin/events/${eventId}/incidents/${incidentId}/respond`, data),
    onSuccess: (response) => {
      toast.success(response.data.notified_reporter 
        ? 'Response sent and reporter notified' 
        : 'Response added successfully');
      setShowRespondDialog(false);
      setResponseMessage('');
      setResponseStatus('');
      setSelectedIncident(null);
      queryClient.invalidateQueries(['incidents', eventId]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to send response');
    }
  });

  // Handle photo upload (public endpoint)
  const handlePhotoUpload = async (file) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Use: JPEG, PNG, WebP, or GIF");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size: 5MB");
      return;
    }
    
    setUploadingPhoto(true);
    
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use public upload endpoint
      const response = await api.post(`/events/${eventId}/incidents/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setNewIncident(prev => ({ ...prev, image_url: response.data.image_url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle camera capture (using device camera)
  const handleCameraCapture = async (imageData) => {
    setUploadingPhoto(true);
    setPhotoPreview(imageData);
    
    try {
      // Convert base64 to blob
      const res = await fetch(imageData);
      const blob = await res.blob();
      const file = new File([blob], `incident_${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/events/${eventId}/incidents/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setNewIncident(prev => ({ ...prev, image_url: response.data.image_url }));
      toast.success("Photo captured and uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload captured photo");
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Track incident by code
  const handleTrackIncident = async () => {
    if (!trackingCode.trim()) {
      setTrackingError('Please enter a tracking code');
      return;
    }
    
    setTrackingError('');
    setTrackedIncident(null);
    
    try {
      const response = await api.get(`/incidents/track/${trackingCode.trim().toUpperCase()}`);
      setTrackedIncident(response.data);
    } catch (err) {
      setTrackingError(err.response?.data?.detail || 'Incident not found');
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url}`;
  };

  const filteredIncidents = incidents?.filter(inc => {
    if (filter === 'all') return true;
    return inc.status === filter;
  });

  const severityConfig = {
    low: { color: 'bg-blue-500', label: 'Low' },
    medium: { color: 'bg-yellow-500', label: 'Medium' },
    high: { color: 'bg-orange-500', label: 'High' },
    critical: { color: 'bg-red-500', label: 'Critical' },
  };

  const stats = {
    total: incidents?.length || 0,
    open: incidents?.filter(i => i.status === 'open').length || 0,
    investigating: incidents?.filter(i => i.status === 'investigating').length || 0,
    resolved: incidents?.filter(i => i.status === 'resolved').length || 0,
    critical: incidents?.filter(i => i.severity === 'critical' && i.status !== 'resolved').length || 0,
  };

  return (
    <div className="space-y-6">
      {/* Critical Alert Banner */}
      {stats.critical > 0 && isAdmin && (
        <Alert variant="destructive">
          <Siren className="h-4 w-4" />
          <AlertTitle>Critical Incidents Active</AlertTitle>
          <AlertDescription>
            {stats.critical} critical incident(s) require immediate attention.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats - Admin Only */}
      {isAdmin && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-2xl font-bold">{stats.open}</div>
              <div className="text-sm text-slate-500">Open</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-2xl font-bold">{stats.investigating}</div>
              <div className="text-sm text-slate-500">Investigating</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-2xl font-bold">{stats.resolved}</div>
              <div className="text-sm text-slate-500">Resolved</div>
            </CardContent>
          </Card>
          <Card className={stats.critical > 0 ? 'border-red-500 border-2' : ''}>
            <CardContent className="pt-4 text-center">
              <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
              <div className="text-sm text-slate-500">Critical</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        {isAdmin && (
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Incidents</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowTrackIncident(true)} variant="outline" data-testid="track-incident-btn">
            <Search className="h-4 w-4 mr-2" /> Track Report
          </Button>
          <Button onClick={() => setShowReportIncident(true)} variant={isAdmin ? 'default' : 'destructive'} data-testid="report-incident-btn">
            <AlertTriangle className="h-4 w-4 mr-2" /> Report Incident
          </Button>
        </div>
      </div>

      {/* Incidents List - Admin Only */}
      {isAdmin && (
        <div className="space-y-4">
          {filteredIncidents?.map((incident) => {
            const severity = severityConfig[incident.severity] || severityConfig.medium;
            return (
              <Card key={incident.id} className={incident.severity === 'critical' ? 'border-red-500 border-2' : ''} data-testid={`incident-card-${incident.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{incident.title}</CardTitle>
                      <CardDescription className="flex flex-wrap items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(incident.created_at).toLocaleString()}
                        </span>
                        {incident.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {incident.location}
                          </span>
                        )}
                        {incident.tracking_code && (
                          <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                            #{incident.tracking_code}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Badge className={severity.color}>{severity.label}</Badge>
                      <Badge variant="outline">{incident.status}</Badge>
                      {incident.is_anonymous && (
                        <Badge variant="secondary"><EyeOff className="h-3 w-3 mr-1" />Anonymous</Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4">{incident.description}</p>
                  
                  {/* Reporter Info */}
                  {!incident.is_anonymous && (incident.reporter_name || incident.reporter_email) && (
                    <div className="bg-slate-50 p-3 rounded-lg mb-4 text-sm">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <User className="h-4 w-4" /> Reporter Information
                      </h4>
                      {incident.reporter_name && <p>Name: {incident.reporter_name}</p>}
                      {incident.reporter_email && <p>Email: {incident.reporter_email}</p>}
                      {incident.reporter_phone && <p>Phone: {incident.reporter_phone}</p>}
                    </div>
                  )}
                  
                  {/* Incident Photo */}
                  {incident.image_url && (
                    <div className="mb-4">
                      <img 
                        src={getImageUrl(incident.image_url)} 
                        alt="Incident" 
                        className="w-full max-w-md rounded-lg border"
                      />
                    </div>
                  )}
                  
                  {/* AI Analysis */}
                  {incident.ai_analysis && (
                    <div className="bg-slate-50 p-3 rounded-lg mb-4">
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                        <Shield className="h-4 w-4" /> AI Analysis
                      </h4>
                      <p className="text-sm text-slate-600">{incident.ai_analysis.summary}</p>
                    </div>
                  )}

                  {/* Previous Updates/Responses */}
                  {incident.updates && incident.updates.length > 0 && (
                    <div className="bg-green-50 p-3 rounded-lg mb-4">
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" /> Response History ({incident.updates.length})
                      </h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {incident.updates.map((update, idx) => (
                          <div key={idx} className="text-sm bg-white p-2 rounded border">
                            <p className="text-slate-600">{update.message}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              By {update.responded_by} - {new Date(update.responded_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant="secondary"
                      onClick={() => {
                        setSelectedIncident(incident);
                        setResponseStatus(incident.status);
                        setShowRespondDialog(true);
                      }}
                      data-testid={`respond-btn-${incident.id}`}
                    >
                      <MessageSquare className="h-4 w-4 mr-1" /> Respond
                    </Button>
                    {incident.status === 'open' && (
                      <Button size="sm" onClick={() => updateMutation.mutate({ incidentId: incident.id, updates: { status: 'investigating' } })}>
                        Start Investigation
                      </Button>
                    )}
                    {incident.status !== 'resolved' && (
                      <Button size="sm" variant="outline" onClick={() => updateMutation.mutate({ incidentId: incident.id, updates: { status: 'resolved' } })}>
                        <CheckCircle className="h-4 w-4 mr-1" /> Resolve
                      </Button>
                    )}
                    {incident.severity !== 'critical' && incident.status !== 'resolved' && (
                      <Button size="sm" variant="destructive" onClick={() => updateMutation.mutate({ incidentId: incident.id, updates: { severity: 'critical' } })}>
                        Escalate to Critical
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {(!incidents || incidents.length === 0) && (
            <Card>
              <CardContent className="py-12 text-center">
                <Shield className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <p className="text-slate-600">No incidents reported</p>
                <p className="text-sm text-slate-500">All clear!</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* For non-admin: Show info card */}
      {!isAdmin && (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg">Safety First</h3>
            <p className="text-slate-600 mt-2">If you see something, say something.</p>
            <p className="text-sm text-slate-500 mt-1">You can report anonymously. We take all reports seriously.</p>
          </CardContent>
        </Card>
      )}

      {/* Report Incident Dialog */}
      <Dialog open={showReportIncident} onOpenChange={(open) => {
        setShowReportIncident(open);
        if (!open) {
          setPhotoPreview(null);
          setNewIncident({
            title: '',
            description: '',
            location: '',
            image_url: '',
            is_anonymous: true,
            reporter_name: '',
            reporter_email: '',
            reporter_phone: '',
            severity: 'medium'
          });
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Report Safety Incident
            </DialogTitle>
            <DialogDescription>
              Your report will be sent to the safety team immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Anonymous Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                {newIncident.is_anonymous ? (
                  <EyeOff className="h-5 w-5 text-slate-500" />
                ) : (
                  <Eye className="h-5 w-5 text-blue-500" />
                )}
                <div>
                  <Label className="text-base">Report Anonymously</Label>
                  <p className="text-xs text-slate-500">
                    {newIncident.is_anonymous 
                      ? "Your identity will be hidden" 
                      : "Share your contact info to receive updates"}
                  </p>
                </div>
              </div>
              <Switch
                checked={newIncident.is_anonymous}
                onCheckedChange={(checked) => setNewIncident({ ...newIncident, is_anonymous: checked })}
                data-testid="anonymous-toggle"
              />
            </div>

            {/* Contact Fields - Only show if NOT anonymous */}
            {!newIncident.is_anonymous && (
              <div className="space-y-3 p-4 border rounded-lg bg-blue-50/50">
                <p className="text-sm text-blue-700 font-medium">Your Contact Information (for updates)</p>
                <div>
                  <Label className="flex items-center gap-1"><User className="h-3 w-3" /> Name</Label>
                  <Input
                    value={newIncident.reporter_name}
                    onChange={(e) => setNewIncident({ ...newIncident, reporter_name: e.target.value })}
                    placeholder="Your name"
                    data-testid="reporter-name"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Mail className="h-3 w-3" /> Email *</Label>
                  <Input
                    type="email"
                    value={newIncident.reporter_email}
                    onChange={(e) => setNewIncident({ ...newIncident, reporter_email: e.target.value })}
                    placeholder="your@email.com"
                    data-testid="reporter-email"
                  />
                  <p className="text-xs text-slate-500 mt-1">Required to receive status updates</p>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Phone className="h-3 w-3" /> Phone (optional)</Label>
                  <Input
                    type="tel"
                    value={newIncident.reporter_phone}
                    onChange={(e) => setNewIncident({ ...newIncident, reporter_phone: e.target.value })}
                    placeholder="+1234567890"
                    data-testid="reporter-phone"
                  />
                </div>
              </div>
            )}

            <div>
              <Label>Title *</Label>
              <Input
                value={newIncident.title}
                onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
                placeholder="Brief description of the incident"
                data-testid="incident-title"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={newIncident.description}
                onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                placeholder="Provide as much detail as possible..."
                rows={4}
                data-testid="incident-description"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={newIncident.location}
                onChange={(e) => setNewIncident({ ...newIncident, location: e.target.value })}
                placeholder="Where did this occur?"
                data-testid="incident-location"
              />
            </div>
            <div>
              <Label>Severity</Label>
              <Select 
                value={newIncident.severity} 
                onValueChange={(val) => setNewIncident({ ...newIncident, severity: val })}
              >
                <SelectTrigger data-testid="incident-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - Minor issue</SelectItem>
                  <SelectItem value="medium">Medium - Needs attention</SelectItem>
                  <SelectItem value="high">High - Urgent</SelectItem>
                  <SelectItem value="critical">Critical - Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Photo Upload Section */}
            <div>
              <Label>Photo Evidence (optional)</Label>
              <p className="text-xs text-slate-500 mb-2">Upload a photo from your device</p>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                {photoPreview || newIncident.image_url ? (
                  <div className="relative">
                    <img 
                      src={photoPreview || getImageUrl(newIncident.image_url)} 
                      alt="Incident preview" 
                      className="w-full h-40 object-cover rounded-lg"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setPhotoPreview(null);
                        setNewIncident(prev => ({ ...prev, image_url: '' }));
                      }}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center space-y-3">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handlePhotoUpload(e.target.files[0])}
                    />
                    <Camera className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                    <div className="flex gap-2 justify-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCameraCapture(true)}
                        disabled={uploadingPhoto}
                        data-testid="camera-capture-btn"
                      >
                        <Smartphone className="h-4 w-4 mr-2" />
                        Take Photo
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        data-testid="upload-photo-btn"
                      >
                        {uploadingPhoto ? (
                          <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Uploading...</>
                        ) : (
                          <><Upload className="h-4 w-4 mr-2" /> Upload Photo</>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">Use your device camera or upload from gallery</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportIncident(false)}>Cancel</Button>
            <Button
              onClick={() => reportMutation.mutate(newIncident)}
              disabled={!newIncident.title || !newIncident.description || reportMutation.isPending || (!newIncident.is_anonymous && !newIncident.reporter_email)}
              variant="destructive"
              data-testid="submit-incident-btn"
            >
              {reportMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Submit Report</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Track Incident Dialog */}
      <Dialog open={showTrackIncident} onOpenChange={(open) => {
        setShowTrackIncident(open);
        if (!open) {
          setTrackingCode('');
          setTrackedIncident(null);
          setTrackingError('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Track Your Incident
            </DialogTitle>
            <DialogDescription>
              Enter the tracking code you received when submitting your report.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Input
                value={trackingCode}
                onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
                placeholder="Enter tracking code (e.g., A1B2C3D4)"
                className="font-mono"
                data-testid="tracking-code-input"
              />
              <Button onClick={handleTrackIncident} data-testid="track-submit-btn">
                <Search className="h-4 w-4" />
              </Button>
            </div>
            
            {trackingError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{trackingError}</AlertDescription>
              </Alert>
            )}
            
            {trackedIncident && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold">{trackedIncident.title}</h4>
                    <Badge className={severityConfig[trackedIncident.severity]?.color || 'bg-slate-500'}>
                      {trackedIncident.severity}
                    </Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status:</span>
                      <Badge variant="outline" className="capitalize">{trackedIncident.status}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Reported:</span>
                      <span>{new Date(trackedIncident.created_at).toLocaleDateString()}</span>
                    </div>
                    {trackedIncident.updated_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Last Updated:</span>
                        <span>{new Date(trackedIncident.updated_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Response History */}
                {trackedIncident.updates && trackedIncident.updates.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" /> Updates
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {trackedIncident.updates.map((update, idx) => (
                        <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-sm">{update.message}</p>
                          <p className="text-xs text-slate-500 mt-2">
                            {new Date(update.responded_at).toLocaleString()} by {update.responded_by}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {(!trackedIncident.updates || trackedIncident.updates.length === 0) && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No updates yet. You'll be notified when there's a response.
                  </p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Respond to Incident Dialog - Admin Only */}
      <Dialog open={showRespondDialog} onOpenChange={(open) => {
        setShowRespondDialog(open);
        if (!open) {
          setSelectedIncident(null);
          setResponseMessage('');
          setResponseStatus('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Respond to Incident
            </DialogTitle>
            {selectedIncident && (
              <DialogDescription>
                Incident #{selectedIncident.tracking_code}: {selectedIncident.title}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Your Response *</Label>
              <Textarea
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder="Describe the actions taken or provide an update..."
                rows={4}
                data-testid="response-message"
              />
            </div>
            <div>
              <Label>Update Status</Label>
              <Select value={responseStatus} onValueChange={setResponseStatus}>
                <SelectTrigger data-testid="response-status">
                  <SelectValue placeholder="Keep current status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Notify Reporter Toggle */}
            {selectedIncident && !selectedIncident.is_anonymous && selectedIncident.reporter_email && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <Label className="text-sm">Notify Reporter via Email</Label>
                  <p className="text-xs text-slate-500">{selectedIncident.reporter_email}</p>
                </div>
                <Switch
                  checked={notifyReporter}
                  onCheckedChange={setNotifyReporter}
                  data-testid="notify-reporter-toggle"
                />
              </div>
            )}
            
            {selectedIncident?.is_anonymous && (
              <p className="text-sm text-slate-500 italic">
                This incident was reported anonymously. The reporter cannot be notified.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRespondDialog(false)}>Cancel</Button>
            <Button
              onClick={() => respondMutation.mutate({
                incidentId: selectedIncident?.id,
                data: {
                  message: responseMessage,
                  new_status: responseStatus || null,
                  notify_reporter: notifyReporter
                }
              })}
              disabled={!responseMessage.trim() || respondMutation.isPending}
              data-testid="send-response-btn"
            >
              {respondMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send Response</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Local Camera Capture for Incident Photos */}
      <LocalCameraCapture
        isOpen={showCameraCapture}
        onClose={() => setShowCameraCapture(false)}
        onCapture={handleCameraCapture}
        title="Capture Incident Photo"
        guideLine="Point camera at the incident area"
      />
    </div>
  );
};

export default SafetyCenter;
